// document_read — ouvrir un fichier et en sortir du texte.
//
// ── CE MODULE NE DÉCIDE RIEN ────────────────────────────────────────────────
//
// Les formats acceptés, les bornes, les refus, la lecture du ZIP, le XML de Word et
// l'assemblage du texte d'un PDF vivent dans src/lib/document.ts, en fonctions pures,
// éprouvées par 41 tests et 30 mutations. Ici il n'y a que ce qu'on ne peut pas éprouver
// sans navigateur : ouvrir un fichier, décompresser, et charger la librairie du PDF.
//
// ── POURQUOI LA LIBRAIRIE DU PDF EST CHARGÉE À LA DEMANDE ───────────────────
//
// Elle pèse plus que toute la console. `import()` dans la fonction — et non en tête de
// fichier — fait que personne ne la télécharge avant d'avoir joint un PDF. C'est la raison
// pour laquelle cette fonction est `async` au-delà de la lecture du fichier.
//
// ── ET POURQUOI RIEN NE LÈVE ────────────────────────────────────────────────
//
// Un fichier renommé, un téléversement tronqué, un .docx qu'un autre outil a écrit
// autrement : tout ça arrive dans l'onglet de quelqu'un. Chacun devient un refus qui se LIT
// (`DocumentRefusal`), jamais une erreur qui casse l'écran.
import {
  DOC_MAX_BYTES,
  type DocumentRefusal,
  documentKind,
  docxBodyText,
  pdfItemsToText,
  pdfPagesToText,
  zipFileBytes,
} from "./document";

export type ReadResult =
  | { readonly ok: true;  readonly text: string; readonly reason?: undefined }
  | { readonly ok: false; readonly reason: DocumentRefusal; readonly text?: undefined };

/** Le chemin d'un .docx vers son texte. Nom fixé par le format OOXML. */
const DOCX_ENTREE = "word/document.xml";

/** Décompresse des octets `deflate-raw`. Le format du ZIP, donc celui du .docx. */
async function inflate(data: Uint8Array): Promise<string> {
  const ds = new DecompressionStream("deflate-raw");
  const w = ds.writable.getWriter();
  void w.write(data);
  void w.close();
  return new TextDecoder().decode(await new Response(ds.readable).arrayBuffer());
}

/** Le XML du document d'un .docx, ou `null` si le fichier n'en est pas un. */
async function docxXml(file: File): Promise<string | null> {
  const octets = new Uint8Array(await file.arrayBuffer());
  const entree = zipFileBytes(octets, DOCX_ENTREE);
  if (entree === null) return null;
  // Une entrée STOCKÉE est rare mais légale ; la supposer compressée rendrait du charabia.
  return entree.method === 0 ? new TextDecoder().decode(entree.data) : await inflate(entree.data);
}

/** Le texte d'un PDF, page par page.
 *
 *  LES OPTIONS SONT CELLES QUE CETTE VERSION DÉCLARE, lues dans ses types le 2026-10-05 et
 *  non supposées : `isEvalSupported` n'existe plus en 6.2.108 (la librairie n'évalue plus de
 *  code), et `destroy()` est sur la TÂCHE de chargement, pas sur le document — d'où la tâche
 *  gardée de côté. `enableXfa` reste à son défaut, faux : un formulaire XFA est une surface
 *  entière dont on n'a pas besoin pour lire du texte.
 *
 *  `disableFontFace` : on ne peint rien, donc installer les polices du fichier serait du
 *  travail et une surface pour rien. */
async function pdfTexte(file: File): Promise<string> {
  const pdfjs = await import("pdfjs-dist");
  // Le travailleur est servi comme un fichier du paquet (`?url` est résolu par le
  // constructeur) : il n'est téléchargé qu'ici, donc seulement par qui joint un PDF.
  const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const tache = pdfjs.getDocument({
    data: new Uint8Array(await file.arrayBuffer()),
    disableFontFace: true,
  });
  try {
    const doc = await tache.promise;
    const pages: string[] = [];
    for (let n = 1; n <= doc.numPages; n++) {
      const page = await doc.getPage(n);
      pages.push(pdfItemsToText((await page.getTextContent()).items as never));
    }
    return pdfPagesToText(pages);
  } finally {
    // DANS LE `finally` : un PDF abîmé lève au milieu des pages, et sans ça le travailleur
    // et la mémoire du document resteraient en vie dans l'onglet jusqu'au rechargement.
    await tache.destroy().catch(() => { /* rien à faire si elle refuse */ });
  }
}

/**
 * Le texte d'un fichier joint, ou la raison du refus.
 *
 * NE LÈVE JAMAIS.
 */
export async function readDocumentText(file: File): Promise<ReadResult> {
  const kind = documentKind(file.type ?? "", file.name ?? "");
  if (kind === null) return { ok: false, reason: "bad-type" };
  // LE POIDS EST JUGÉ AVANT D'OUVRIR : il ne borne pas le coût (c'est le travail du plafond
  // de caractères) mais il empêche de charger 500 Mo dans l'onglet pour en jeter 99 %.
  if (file.size > DOC_MAX_BYTES) return { ok: false, reason: "too-big" };
  try {
    const texte = kind === "text" ? await file.text()
                : kind === "pdf"  ? await pdfTexte(file)
                : (await docxXml(file).then((x) => (x === null ? null : docxBodyText(x))));
    if (texte === null) return { ok: false, reason: "unreadable" };
    // RIEN DE LISIBLE : le cas fréquent du PDF scanné. Le refus renvoie vers la photo, qui
    // marche déjà (`documentRefusal("no-text")`).
    if (texte.trim() === "") return { ok: false, reason: "no-text" };
    return { ok: true, text: texte };
  } catch {
    return { ok: false, reason: "unreadable" };
  }
}
